import React from 'react';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useTranslation } from 'react-i18next';

import { IBrandingListModalProps } from './interfaces';
import { useStyles } from './styles';

const BrandingListModal = ({
  open,
  onClose,
  onLeaveBrandingMode,
  onCreateNew,
  brandings,
  brandingsLoading = false,
  onToggleBranding,
  onEditBranding,
  onCloneBranding,
  onDeleteBranding,
  currentBrandingId,
}: IBrandingListModalProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const handleEdit = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onEditBranding?.(id);
  };

  const handleClone = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onCloneBranding?.(id);
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onDeleteBranding?.(id);
  };

  return (
    <Modal open={open} onClose={onClose} sx={classes.modalRoot}>
      <Box sx={classes.container}>
        <Box sx={classes.header}>
          <Typography sx={classes.title}>
            {translate(
              'adminToolbox.availableBrandings',
              'Available brandings',
            )}{' '}
            <Typography component="span" sx={classes.titleCount}>
              ({brandings.length})
            </Typography>
          </Typography>
          <IconButton onClick={onClose} sx={classes.closeButton} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={classes.content}>
          {brandingsLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '32px 16px',
              }}
            >
              <CircularProgress size={24} />
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 14,
                  textAlign: 'center',
                }}
              >
                {translate(
                  'adminToolbox.loadingBrandings',
                  'Loading brandings...',
                )}
              </Typography>
            </Box>
          ) : brandings.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '32px 16px',
              }}
            >
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 14,
                  textAlign: 'center',
                }}
              >
                {translate(
                  'adminToolbox.noBrandingsYet',
                  'No brandings yet. Create your first one.',
                )}
              </Typography>
            </Box>
          ) : (
            <Box sx={classes.brandingList}>
              {brandings.map((branding) => {
                const isCurrent =
                  currentBrandingId != null
                    ? branding.id === currentBrandingId
                    : branding.isActive;

                return (
                  <Box key={branding.id} sx={classes.brandingItem}>
                    <Typography sx={classes.brandingName}>
                      {branding.name === 'Unnamed'
                        ? translate('adminToolbox.unnamed', 'Unnamed')
                        : branding.name}
                      {isCurrent && (
                        <Typography component="span" sx={classes.currentLabel}>
                          {' '}
                          ({translate('adminToolbox.current', 'Current')})
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={classes.brandingItemRight}>
                      <Box sx={classes.actionButtons}>
                        <IconButton
                          onClick={(e) => handleEdit(e, branding.id)}
                          sx={classes.actionButton}
                          size="small"
                          aria-label={translate('common.edit', 'Edit')}
                        >
                          <EditOutlinedIcon sx={classes.actionIcon} />
                        </IconButton>
                        <IconButton
                          onClick={(e) => handleClone(e, branding.id)}
                          sx={classes.actionButton}
                          size="small"
                          aria-label={translate('common.copy', 'Copy')}
                        >
                          <ContentCopyOutlinedIcon sx={classes.actionIcon} />
                        </IconButton>
                        <IconButton
                          onClick={(e) => handleDelete(e, branding.id)}
                          sx={classes.actionButton}
                          size="small"
                          aria-label={translate('common.delete', 'Delete')}
                        >
                          <DeleteOutlineIcon sx={classes.actionIcon} />
                        </IconButton>
                      </Box>

                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleBranding(branding.id);
                        }}
                        sx={[
                          classes.selectButton,
                          isCurrent && classes.selectButtonActive,
                        ]}
                        size="small"
                        aria-label={
                          isCurrent
                            ? translate('adminToolbox.current', 'Current')
                            : translate(
                                'adminToolbox.activateBranding',
                                'Activate branding',
                              )
                        }
                      >
                        {isCurrent ? (
                          <CheckIcon sx={classes.selectIconActive} />
                        ) : (
                          <PowerSettingsNewIcon
                            sx={classes.selectIconInactive}
                          />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box sx={classes.footer}>
          <Button
            variant="outlined"
            onClick={onLeaveBrandingMode}
            sx={classes.leaveButton}
          >
            {translate('adminToolbox.leaveBrandingMode', 'Leave branding mode')}
          </Button>
          <Button
            variant="contained"
            onClick={onCreateNew}
            sx={classes.createButton}
          >
            {translate('adminToolbox.createNewBranding', 'Create new branding')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default BrandingListModal;
